import * as React from 'react'
import { useClickOutside } from '../effects/ClickOutside'
import {
  DelayedToggleConfig,
  DelayedToggleProvider,
  DelayedToggleContext,
  consumeDelayedToggle,
} from '../effects/DelayedToggle'
import { Trigger, TriggerConfig } from '../effects/Trigger'
import { OverlayTriggerVariantNS } from '../state/OverlayTrigger'
import { useVisibility, VisibilityConfig } from '../state/Visibility'
import { wrapPromise } from '../state/DelayedCallback'
import { Overlay, OverlayProps } from './Overlay'
import { Omit } from '../utils/types'

/* TYPES */

interface OverlayTriggerNextProps
  extends Omit<OverlayProps, 'target'>,
    VisibilityConfig {
  afterHide?: DelayedToggleConfig['afterDisable']
  afterShow?: DelayedToggleConfig['afterEnable']
  beforeHide?: DelayedToggleConfig['beforeDisable']
  beforeShow?: DelayedToggleConfig['beforeEnable']
  hideDelay?: DelayedToggleConfig['disableDelay']
  overlay: OverlayProps['children']
  showDelay?: DelayedToggleConfig['enableDelay']
  trigger?: TriggerConfig['trigger']
}

export const OverlayTriggerNext = React.forwardRef<
  HTMLElement,
  OverlayTriggerNextProps
>(
  (
    {
      afterHide = () => {},
      afterShow = () => {},
      beforeHide = () => true,
      beforeShow = () => true,
      canFlip,
      children,
      hideDelay = 100,
      initialIsVisible,
      overlay,
      padding,
      placement,
      showDelay,
      shrink,
      trigger = OverlayTriggerVariantNS.click,
    }: OverlayTriggerNextProps,
    ref
  ) => {
    const refOverlay = React.useRef(null)
    const refTarget =
      (ref as React.RefObject<HTMLElement>) || React.useRef<HTMLElement>(null)

    const visibility = useVisibility({ initialIsVisible })

    useClickOutside({
      onClickOutside: event => {
        if (visibility.isVisible) {
          return wrapPromise(beforeHide(event)).then(
            (shouldUpdate: boolean) => {
              if (shouldUpdate !== false) {
                visibility.hide()
                afterHide()
              }
            }
          )
        }
      },
      refs: [refOverlay, refTarget],
    })

    const restoreFocus = () => {
      refTarget.current && refTarget.current.focus()
    }

    return (
      <DelayedToggleProvider
        {...{
          enableDelay: showDelay,
          beforeEnable: beforeShow,
          beforeDisable: beforeHide,
          disableDelay: hideDelay,
          isEnabled: visibility.isVisible,
          onDisable: visibility.hide,
          afterDisable: () => {
            afterHide()
            restoreFocus()
          },
          onEnable: visibility.show,
          afterEnable: afterShow,
        }}
      >
        <DelayedToggleContext.Consumer>
          {({ enable, disable }) => (
            <>
              <Trigger
                {...{
                  ref: refTarget,
                  isEnabled: visibility.isVisible,
                  enable,
                  disable,
                  enableKeys: ['ArrowDown', 'Down', 'Enter', 'Space', ' '],
                  disableKeys: ['Escape', 'Esc'],
                  trigger,
                }}
              >
                {children}
              </Trigger>
              {visibility.isVisible && (
                <Trigger
                  {...{
                    ref: refOverlay,
                    isEnabled: visibility.isVisible,
                    enable,
                    disable,
                    disableKeys: ['Escape', 'Esc', 'Tab'],
                    trigger:
                      trigger === OverlayTriggerVariantNS.click
                        ? OverlayTriggerVariantNS.none
                        : trigger,
                  }}
                >
                  <Overlay
                    {...{
                      ref: refOverlay,
                      placement,
                      target: refTarget,
                      padding,
                      canFlip,
                      shrink,
                    }}
                  >
                    {overlay}
                  </Overlay>
                </Trigger>
              )}
            </>
          )}
        </DelayedToggleContext.Consumer>
      </DelayedToggleProvider>
    )
  }
)

export const consumeOverlayTrigger = () => {
  const delayedToggle = consumeDelayedToggle()
  return {
    overlay: {
      toggle: delayedToggle.toggle,
      show: delayedToggle.enable,
      hide: delayedToggle.disable,
    },
  }
}
